import {
  Alert,
  Button,
  CloseButton,
  Divider,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  BrandGithub,
  BrandGoogle,
  BrandWindows,
  ExclamationMark,
} from 'tabler-icons-react';
import { z } from 'zod';

import { useAuth } from 'api/auth';
import { API_URL } from 'config/env';
import storage from 'utils/storage';

import { LoginFormValues } from '../types';
import AuthLayout from './AuthLayout';

const apiBase = API_URL.replace(/\/$/, '');

export function LoginForm() {
  const { t } = useTranslation();
  const {
    login,
    isLoggingIn,
    verifyMfaLogin,
    isVerifyingMfa,
    providers,
  } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const loginSchema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .email({ message: t('auth.validation.emailInvalid') })
          .min(1, { message: t('auth.validation.emailRequired') }),
        password: z
          .string()
          .min(6, { message: t('auth.validation.passwordMin') })
          .min(1, { message: t('auth.validation.passwordRequired') }),
      }),
    [t]
  );

  const mfaSchema = useMemo(
    () =>
      z.object({
        code: z
          .string()
          .trim()
          .min(1, { message: t('auth.validation.codeRequired') }),
      }),
    [t]
  );

  const form = useForm<LoginFormValues>({
    initialValues: { email: '', password: '' },
    validate: zodResolver(loginSchema),
  });

  const mfaForm = useForm<{ code: string }>({
    initialValues: { code: '' },
    validate: zodResolver(mfaSchema),
  });

  const [showFormError, setShowFormError] = useState(false);
  const [mfaState, setMfaState] = useState<{
    userId: string;
    method: 'email' | 'totp';
  } | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(
      window.location.search,
    );
    const token = params.get('token');
    const error = params.get('error');
    if (token) {
      storage.setToken(token);
      queryClient
        .invalidateQueries({ queryKey: ['auth-user'] })
        .catch(() => {});
      const state = location.state as { path?: string };
      navigate(state?.path || '/dashboard', {
        replace: true,
      });
      window.history.replaceState(
        {},
        '',
        window.location.pathname,
      );
      return;
    }
    if (error) {
      window.history.replaceState(
        {},
        '',
        window.location.pathname,
      );
      showNotification({
        title: t('auth.notifications.signInFailed'),
        message:
          error === 'oauth_failed'
            ? t('auth.notifications.oauthFailed')
            : t('auth.notifications.genericError'),
        color: 'red',
        icon: <ExclamationMark />,
      });
    }
  }, [navigate, location.state, queryClient, t]);

  const handleSubmit = (values: LoginFormValues) => {
    login(values, {
      onSuccess: (data) => {
        if ('mfaRequired' in data) {
          setShowFormError(false);
          setMfaState({
            userId: data.userId,
            method: data.mfaMethod,
          });
          mfaForm.setValues({ code: '' });
          return;
        }
        const state = location.state as { path: string };
        setShowFormError(false);
        setMfaState(null);
        const user =
          'user' in data ? data.user : null;
        const goToAdmin =
          user?.role === 'super admin' && !user?.org;
        navigate(
          state?.path
            || (goToAdmin
              ? '/admin-settings'
              : '/dashboard'),
          { replace: true },
        );
      },
      onError: (err) => {
        if (axios.isAxiosError(err)) {
          if (err.response?.status === 401) {
            setShowFormError(true);
            return;
          }
          if (err.response?.status === 403) {
            const msg = (
              err.response?.data as {
                message?: string;
              }
            )?.message;
            showNotification({
              title: t('auth.notifications.signInNotAllowed'),
              message: msg || t('auth.notifications.accountCannotSignIn'),
              color: 'red',
              icon: <ExclamationMark />,
            });
            return;
          }
        }
        showNotification({
          title: t('common.serverError'),
          message: t('common.tryAgainLater'),
          color: 'red',
          icon: <ExclamationMark />,
        });
      },
    });
  };

  const handleMfaSubmit = (values: { code: string }) => {
    if (!mfaState) return;
    verifyMfaLogin(
      { userId: mfaState.userId, code: values.code },
      {
        onSuccess: (data) => {
          const state = location.state as {
            path: string;
          };
          setMfaState(null);
          const user = data?.user;
          const goToAdmin =
            user?.role === 'super admin' && !user?.org;
          navigate(
            state?.path
              || (goToAdmin
                ? '/admin-settings'
                : '/dashboard'),
            { replace: true },
          );
        },
        onError: () => {
          showNotification({
            title: t('auth.notifications.verificationFailed'),
            message: t('auth.notifications.checkCode'),
            color: 'red',
            icon: <ExclamationMark />,
          });
        },
      },
    );
  };

  const hasOAuth =
    providers?.google
    || providers?.github
    || providers?.microsoft;

  return (
    <AuthLayout
      title={mfaState ? 'Verify Sign In' : 'Sign In'}
      subtitle={
        mfaState
          ? undefined
          : t('auth.login.welcomeSubtitle')
      }
    >
      {mfaState ? (
        <form onSubmit={mfaForm.onSubmit(handleMfaSubmit)}>
          <Text c="dimmed" size="sm" mb="lg">
            {mfaState.method === 'email'
              ? t('auth.login.mfaEmailHint')
              : t('auth.login.mfaTotpHint')}
          </Text>
          <TextInput
            label={t('auth.login.verificationCode')}
            placeholder="000000"
            mb="lg"
            radius="md"
            size="md"
            {...mfaForm.getInputProps('code')}
          />
          <Group position="right" spacing="sm">
            <Button
              variant="subtle"
              color="gray"
              radius="md"
              onClick={() => {
                setMfaState(null);
                mfaForm.setValues({ code: '' });
              }}
            >
              {t('auth.login.back')}
            </Button>
            <Button
              type="submit"
              loading={isVerifyingMfa}
              radius="md"
              size="md"
              variant="gradient"
              gradient={{ from: 'indigo', to: 'violet' }}
            >
              {t('auth.login.verify')}
            </Button>
          </Group>
        </form>
      ) : (
        <>
          {hasOAuth && (
            <>
              <Group grow spacing="sm" mb="lg">
                {providers?.google && (
                  <Button
                    component="a"
                    href={`${apiBase}/auth/google`}
                    variant="default"
                    radius="md"
                    size="md"
                    leftIcon={
                      <BrandGoogle size={18} />
                    }
                    sx={(theme) => ({
                      borderColor:
                        theme.colorScheme === 'dark'
                          ? '#333'
                          : '#e5e7eb',
                    })}
                  >
                    {t('auth.oauth.google')}
                  </Button>
                )}
                {providers?.github && (
                  <Button
                    component="a"
                    href={`${apiBase}/auth/github`}
                    variant="default"
                    radius="md"
                    size="md"
                    leftIcon={
                      <BrandGithub size={18} />
                    }
                    sx={(theme) => ({
                      borderColor:
                        theme.colorScheme === 'dark'
                          ? '#333'
                          : '#e5e7eb',
                    })}
                  >
                    {t('auth.oauth.github')}
                  </Button>
                )}
                {providers?.microsoft && (
                  <Button
                    component="a"
                    href={`${apiBase}/auth/microsoft`}
                    variant="default"
                    radius="md"
                    size="md"
                    leftIcon={
                      <BrandWindows size={18} />
                    }
                    sx={(theme) => ({
                      borderColor:
                        theme.colorScheme === 'dark'
                          ? '#333'
                          : '#e5e7eb',
                    })}
                  >
                    {t('auth.oauth.microsoft')}
                  </Button>
                )}
              </Group>
              <Divider
                label={t('auth.login.continueWithEmail')}
                labelPosition="center"
                mb="lg"
                styles={{
                  label: {
                    fontSize: 12,
                    color: '#9ca3af',
                  },
                }}
              />
            </>
          )}

          <form onSubmit={form.onSubmit(handleSubmit)}>
            {showFormError && (
              <Alert
                color="red"
                icon={<AlertCircle color="#E03131" />}
                mb="md"
                radius="md"
                styles={{
                  wrapper: { alignItems: 'center' },
                }}
              >
                <Group noWrap>
                  <Text color="red.9" size="sm">
                    {t('auth.login.incorrectCredentials')}
                  </Text>
                  <CloseButton
                    color="red.9"
                    ml="auto"
                    aria-label={t('auth.login.closeAlert')}
                    onClick={() => setShowFormError(false)}
                  />
                </Group>
              </Alert>
            )}
            <TextInput
              label={t('auth.email')}
              placeholder={t('auth.login.emailPlaceholder')}
              mb="md"
              radius="md"
              size="md"
              {...form.getInputProps('email')}
            />
            <PasswordInput
              label={t('auth.password')}
              placeholder={t('auth.login.passwordPlaceholder')}
              mb="xl"
              radius="md"
              size="md"
              {...form.getInputProps('password')}
            />
            <Button
              type="submit"
              loading={isLoggingIn}
              fullWidth
              size="md"
              radius="md"
              variant="gradient"
              gradient={{ from: 'indigo', to: 'violet' }}
              sx={{
                boxShadow:
                  '0 4px 14px rgba(99,102,241,0.3)',
              }}
            >
              {t('auth.signIn')}
            </Button>
          </form>

          <Stack spacing={6} align="center" mt="lg">
            <Text size="sm" c="dimmed">
              {t('auth.login.noAccount')}
              {' '}
              <Text
                component={Link}
                to="/register"
                size="sm"
                weight={600}
                color="violet.6"
                inherit
              >
                {t('common.register')}
              </Text>
            </Text>
            <Text size="sm" c="dimmed">
              {t('auth.login.needAccess')}
              {' '}
              <Text
                component={Link}
                to="/request-access"
                size="sm"
                weight={600}
                color="violet.6"
                inherit
              >
                {t('common.requestAccess')}
              </Text>
            </Text>
          </Stack>
        </>
      )}
    </AuthLayout>
  );
}
